<template>
  <div class="message-display" ref="messageContainer">
    <div
      v-for="msg in sortedMessages"
      :key="msg.id"
      class="message-row"
      :class="{ 'message-row--user': msg.role === 'user', 'message-row--assistant': msg.role === 'assistant' }"
    >
      <div
        class="message-bubble"
        :class="bubbleClass(msg)"
      >
        <MessageRenderer :message="msg" />
        <FollowUpBubbles
          v-if="msg.role === 'assistant' && isLastAssistantMessage(msg) && !streaming"
          :content="msg.content"
          @select="$emit('followup', $event)"
        />
      </div>
    </div>

    <div v-if="showTypingIndicator" class="message-row message-row--assistant">
      <div class="message-bubble" :class="assistantBubbleClass">
        <span class="typing-indicator">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MessageRenderer from './MessageRenderer.vue';
import FollowUpBubbles from './FollowUpBubbles.vue';

export default {
  name: 'MessageDisplay',
  components: { MessageRenderer, FollowUpBubbles },
  computed: {
    ...mapState('chat', ['messages', 'streaming']),
    ...mapState('darkMode', ['isDarkMode']),
    sortedMessages() {
      return [...this.messages].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
    },
    showTypingIndicator() {
      return this.streaming;
    },
    assistantBubbleClass() {
      return this.isDarkMode ? 'message-bubble--assistant-dark' : 'message-bubble--assistant';
    }
  },
  watch: {
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    }
  },
  updated() {
    this.scrollToBottom();
  },
  methods: {
    bubbleClass(msg) {
      if (msg.role === 'user') return 'message-bubble--user';
      return this.isDarkMode ? 'message-bubble--assistant-dark' : 'message-bubble--assistant';
    },
    isLastAssistantMessage(msg) {
      const assistantMsgs = this.sortedMessages.filter(m => m.role === 'assistant');
      return assistantMsgs.length > 0 && assistantMsgs[assistantMsgs.length - 1].id === msg.id;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.message-display {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (max-width: 767px) {
  .message-display {
    padding: 12px 14px;
  }
}

.message-row {
  display: flex;
}

.message-row--user {
  justify-content: flex-end;
}

.message-row--assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: min(85%, 640px);
  padding: 10px 14px;
  border-radius: 16px;
  word-wrap: break-word;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.message-bubble--user {
  background-color: #F28A3E;
  color: #fff;
}

.message-bubble--assistant {
  background-color: #f0f0f0;
  color: #2c3e50;
}

.message-bubble--assistant-dark {
  background-color: #464d54;
  color: #f0f0f0;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.typing-indicator .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}

.typing-indicator .dot:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
